import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const BASE =
  "group inline-flex items-center justify-center gap-2.5 rounded-full px-7 py-3.5 text-[12px] font-medium uppercase tracking-[0.12em] leading-none transition-all duration-200 select-none hover:-translate-y-0.5";

const VARIANTS = {
  gold: "bg-gold text-white shadow-sm hover:shadow-lg hover:brightness-105",
  outlineWhite: "border border-white/70 text-white hover:bg-white hover:text-navy",
  navy: "bg-navy text-white shadow-sm hover:shadow-lg hover:bg-navylight",
  outlineNavy: "border border-navy text-navy hover:bg-navy hover:text-white",
};

export function Btn({
  variant = "gold",
  href,
  external = false,
  arrow = true,
  className = "",
  testId,
  children,
  onClick,
  type,
}) {
  const cls = `${BASE} ${VARIANTS[variant]} ${className}`;
  const inner = (
    <>
      {children}
      {arrow && (
        <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1">
          →
        </span>
      )}
    </>
  );
  if (href) {
    if (external) {
      return (
        <a data-testid={testId} href={href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onClick}>
          {inner}
        </a>
      );
    }
    if (/^(https?:|mailto:|tel:)/.test(href)) {
      return (
        <a data-testid={testId} href={href} className={cls} onClick={onClick}>
          {inner}
        </a>
      );
    }
    return (
      <Link data-testid={testId} to={href} className={cls} onClick={onClick}>
        {inner}
      </Link>
    );
  }
  return (
    <button data-testid={testId} type={type || "button"} className={cls} onClick={onClick}>
      {inner}
    </button>
  );
}

export function Eyebrow({ children, center = false, className = "" }) {
  return (
    <div
      className={`flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.22em] text-gold ${
        center ? "justify-center" : ""
      } ${className}`}
    >
      <span className="h-px w-8 bg-gold" aria-hidden="true" />
      <span>{children}</span>
      {center && <span className="h-px w-8 bg-gold" aria-hidden="true" />}
    </div>
  );
}

export function SplitHeadline({ as: Tag = "h2", line1, line2, light = false, size = "md", className = "" }) {
  const sizes = {
    h1: "text-4xl sm:text-5xl lg:text-6xl leading-[1.08]",
    md: "text-3xl sm:text-4xl lg:text-5xl leading-[1.12]",
    sm: "text-2xl sm:text-3xl leading-[1.2]",
  };
  return (
    <Tag className={`font-serif font-bold tracking-tight ${sizes[size]} ${light ? "text-white" : "text-navy"} ${className}`}>
      <span className="block">{line1}</span>
      <span className="block font-normal italic text-gold">{line2}</span>
    </Tag>
  );
}

export function ParallaxImage({ src, alt = "", className = "", strength = 60 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = el.parentElement;
    if (!container) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = null;
    let visible = false;

    const update = () => {
      raf = null;
      const rect = container.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      const offset = Math.max(-1, Math.min(1, progress)) * strength;
      el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0) scale(1.22)`;
    };
    const onScroll = () => {
      if (visible && raf == null) raf = requestAnimationFrame(update);
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) onScroll();
      },
      { threshold: 0 }
    );
    io.observe(container);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [strength]);

  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      aria-hidden={alt === "" ? "true" : undefined}
      className={`absolute inset-0 h-full w-full object-cover will-change-transform ${className}`}
      style={{ transform: "scale(1.22)" }}
    />
  );
}

export function Reveal({ as: Tag = "div", className = "", delay = 0, children, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("is-visible");
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`reveal ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}
